import { Paper, Typography, Popper, Fade, Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useCopyEmoji } from '@/hooks/useCopyEmoji';
import { recordEmojiCopy } from '@utils/popularEmojis';
import { EmojiImage } from './EmojiImage';

interface ClickableEmojiTileProps {
  emoji: string;
  name: string;
  size?: 'small' | 'medium' | 'large';
  /** Render the emoji as a Twemoji image instead of the native glyph — for
   * code points too new for most OS fonts to display. Defaults to native
   * rendering so existing callers are unaffected. */
  renderAsImage?: boolean;
}

export const ClickableEmojiTile = ({
  emoji,
  name,
  size = 'medium',
  renderAsImage = false,
}: ClickableEmojiTileProps) => {
  const { t } = useTranslation();
  // Shared copy interaction (clipboard, analytics, popover, auto-close timer).
  // A copy is a copy regardless of page — static-page tiles record popularity too.
  const { copy, anchorEl, open } = useCopyEmoji({ onCopied: recordEmojiCopy });

  const sizeStyles = {
    small: { emoji: '1.8rem', minWidth: 60, padding: 1 },
    medium: { emoji: '2.5rem', minWidth: 80, padding: 2 },
    large: { emoji: '3rem', minWidth: 100, padding: 2 },
  };

  const styles = sizeStyles[size];

  return (
    <>
      <Paper
        elevation={1}
        role="button"
        tabIndex={0}
        aria-label={t('a11y.copyEmoji', { name })}
        onClick={(event) => copy(emoji, event)}
        onKeyDown={(event: React.KeyboardEvent<HTMLElement>) => {
          if (event.key === 'Enter' || event.key === ' ') {
            // Space scrolls the page by default — suppress that.
            if (event.key === ' ') event.preventDefault();
            copy(emoji, event);
          }
        }}
        sx={{
          p: styles.padding,
          textAlign: 'center',
          minWidth: styles.minWidth,
          cursor: 'pointer',
          transition: 'all 0.2s ease',
          '&:hover': {
            elevation: 3,
            bgcolor: 'grey.50',
            transform: 'scale(1.05)',
          },
          '&:focus-visible': {
            transform: 'scale(1.05)',
            bgcolor: 'grey.50',
            outline: '2px solid',
            outlineColor: 'primary.main',
            outlineOffset: '2px',
          },
          '&:active': {
            transform: 'scale(0.98)',
          },
        }}
      >
        {renderAsImage ? (
          <Box sx={{ mb: size === 'small' ? 0.5 : 1 }}>
            <EmojiImage emoji={emoji} fontSize={styles.emoji} />
          </Box>
        ) : (
          <Typography sx={{ fontSize: styles.emoji, mb: size === 'small' ? 0.5 : 1 }}>
            {emoji}
          </Typography>
        )}
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{
            display: 'block',
            fontSize: size === 'small' ? '0.65rem' : '0.75rem',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {name}
        </Typography>
      </Paper>
      <Popper open={open} anchorEl={anchorEl} placement="top" transition>
        {({ TransitionProps }) => (
          <Fade {...TransitionProps} timeout={200}>
            <Paper
              sx={{
                p: 1,
                px: 2,
                bgcolor: 'success.main',
                color: 'white',
                mb: 1,
              }}
            >
              <Typography variant="body2">{t('copied')}</Typography>
            </Paper>
          </Fade>
        )}
      </Popper>
    </>
  );
};
